import {
  BlotterAggFunc,
  CollateralHealthStatusEnum,
  getAgGridColId,
  useDefaultColumnsWithGroups,
  type Column,
  type ColumnDef,
  type ColumnGroupDefinition,
  type ColumnOrColumnGroup,
} from '@talos/kyoko';
import type { IRowNode } from 'ag-grid-community';
import { useMemo } from 'react';
import { CollateralStatusCell } from './CollateralStatusCell';
import {
  isCounterpartyMonitoringCollateralizedDeal,
  type CounterpartyMonitoringRow,
  type CounterpartyMonitoringRowField,
} from './counterpartyMonitoringRows';

export type CounterpartyMonitoringColumn = Exclude<CounterpartyMonitoringRowField, 'rowID' | 'original'>;

const hierarchyColumns = [
  'counterpartyType',
  'Counterparty',
  'MarketAccount',
  'MarginCurrency',
  'productType',
  'Symbol',
] satisfies CounterpartyMonitoringColumn[];

// Columns groups
const exposureOverviewColumns = ['Amount'] satisfies CounterpartyMonitoringColumn[];
const collateralMarginDetailColumns = ['Collateral', 'collateralStatus'] satisfies CounterpartyMonitoringColumn[];
const positionDetailColumns = ['Symbol', 'MarkPrice', 'UnrealizedPnL'] satisfies CounterpartyMonitoringColumn[];
// Backend does not expose date fields yet; keep Figma group scaffold ready for future columns.
const dateColumns = [] satisfies CounterpartyMonitoringColumn[];
const stressColumns = [
  'StressFactor',
  'NegativeStressPnL',
  'PositiveStressPnL',
] satisfies CounterpartyMonitoringColumn[];

// All columns grouped together
const groupedDataColumns = [
  ...exposureOverviewColumns,
  ...collateralMarginDetailColumns,
  ...positionDetailColumns,
  ...dateColumns,
  ...stressColumns,
] satisfies CounterpartyMonitoringColumn[];

const hiddenUngroupedHierarchyColumns = hierarchyColumns.filter(
  column => !(groupedDataColumns as readonly CounterpartyMonitoringColumn[]).includes(column)
);

const defaultGroupedDataColumns = groupedDataColumns.filter(
  column => !(hierarchyColumns as readonly CounterpartyMonitoringColumn[]).includes(column)
);

const hiddenRowGroupColumns = hierarchyColumns.map(field => ({ field, initialHide: true })) satisfies Partial<Column>[];

/** Column tool panel / persistence order matches rendered groups, then hidden row-group cols. */
const defaultCounterpartyMonitoringColumns = [...defaultGroupedDataColumns, ...hiddenRowGroupColumns] satisfies (
  | CounterpartyMonitoringColumn
  | Partial<Column>
)[];

const counterpartyMonitoringColumnGroups: ColumnGroupDefinition[] = [
  {
    groupId: 'exposureOverview',
    headerName: 'Exposure Overview',
    marryChildren: true,
    columnIds: exposureOverviewColumns,
  },
  {
    groupId: 'collateralMarginDetails',
    headerName: 'Collateral/Margin Details',
    marryChildren: true,
    columnIds: collateralMarginDetailColumns,
  },
  {
    groupId: 'positionDetails',
    headerName: 'Position Details',
    marryChildren: true,
    columnIds: positionDetailColumns,
  },
  {
    groupId: 'dates',
    headerName: 'Dates',
    marryChildren: true,
    columnIds: dateColumns,
  },
  {
    groupId: 'stressOverview',
    headerName: 'Stress Overview',
    marryChildren: true,
    columnIds: stressColumns,
  },
];

const rowGroupColumnProps = {
  sortable: true,
  enableRowGroup: true,
  initialRowGroup: true,
  initialHide: true,
  suppressColumnsToolPanel: true,
} as const;

const collateralStatusSeverity: Record<CollateralHealthStatusEnum, number> = {
  [CollateralHealthStatusEnum.Release]: 0,
  [CollateralHealthStatusEnum.Healthy]: 1,
  [CollateralHealthStatusEnum.Warning]: 2,
  [CollateralHealthStatusEnum.MarginCall]: 3,
  [CollateralHealthStatusEnum.LiquidationRisk]: 4,
  [CollateralHealthStatusEnum.Unsecured]: 5,
};

function getCollateralStatusSortValue(
  value: string | null | undefined,
  node: IRowNode<CounterpartyMonitoringRow> | undefined
): string {
  if (value != null) {
    return value;
  }

  const firstLeafData = node?.allLeafChildren?.find(
    child => isCounterpartyMonitoringCollateralizedDeal(child.data) && child.data.collateralStatus != null
  )?.data;

  if (!isCounterpartyMonitoringCollateralizedDeal(firstLeafData)) {
    return '';
  }

  return firstLeafData.collateralStatus ?? '';
}

function getCollateralStatusSeverity(status: string) {
  return collateralStatusSeverity[status as CollateralHealthStatusEnum] ?? -1;
}

export function useCounterpartyMonitoringColumns(
  defaultColumns = defaultCounterpartyMonitoringColumns
): ColumnOrColumnGroup[] {
  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      (
        [
          {
            field: 'counterpartyType',
            type: 'text',
            title: 'Counterparty Type',
            ...rowGroupColumnProps,
            initialRowGroupIndex: 0,
          },
          {
            type: 'counterparty',
            field: 'Counterparty',
            title: 'Counterparty',
            ...rowGroupColumnProps,
            initialRowGroupIndex: 1,
          },
          {
            type: 'marketAccount',
            field: 'MarketAccount',
            title: 'Market Account',
            initialWidth: 150,
            ...rowGroupColumnProps,
            initialRowGroupIndex: 2,
            params: {
              preferMarketAccountDisplayName: true,
            },
          },
          {
            type: 'currency',
            field: 'MarginCurrency',
            title: 'Collateral Asset',
            ...rowGroupColumnProps,
            initialRowGroupIndex: 3,
          },
          {
            type: 'text',
            field: 'productType',
            title: 'Product Type',
            ...rowGroupColumnProps,
            initialRowGroupIndex: 4,
          },
          {
            type: 'security',
            field: 'Symbol',
            title: 'Symbol',
            initialWidth: 150,
            ...rowGroupColumnProps,
            initialRowGroupIndex: 5,
          },
          {
            type: 'size',
            field: 'Amount',
            title: 'Position Qty',
            aggregate: true,
            params: {
              currencyField: 'amountCurrency',
              securityField: 'Symbol',
              securityFieldProperty: 'PositionCurrency',
            },
          },
          {
            type: 'size',
            field: 'Collateral',
            title: 'Collateral',
            aggregate: true,
            initialAggFunc: BlotterAggFunc.MaxSize,
            params: {
              currencyField: 'MarginCurrency',
            },
          },
          {
            type: 'custom',
            field: 'collateralStatus',
            title: 'Collateral Status',
            initialWidth: 320,
            minWidth: 240,
            params: {
              cellRenderer: CollateralStatusCell,
              comparator: (a, b, nodeA, nodeB) =>
                getCollateralStatusSeverity(getCollateralStatusSortValue(a, nodeA)) -
                getCollateralStatusSeverity(getCollateralStatusSortValue(b, nodeB)),
            },
          },
          {
            type: 'size',
            field: 'MarkPrice',
            title: 'Mark Price',
            headerGroupToggle: true,
            columnsInGroup: ['UnrealizedPnL'],
            params: {
              securityField: 'Symbol',
              securityFieldProperty: 'QuoteCurrency',
            },
          },
          {
            type: 'size',
            field: 'UnrealizedPnL',
            title: 'Unrealized P&L',
            aggregate: true,
            params: {
              securityField: 'Symbol',
              securityFieldProperty: 'QuoteCurrency',
              highlightNegative: true,
            },
          },
          {
            type: 'number',
            field: 'StressFactor',
            title: 'Stress Factor',
            initialWidth: 120,
            headerGroupToggle: true,
            columnsInGroup: ['NegativeStressPnL', 'PositiveStressPnL'],
          },
          {
            type: 'size',
            field: 'NegativeStressPnL',
            title: 'Negative Stress P&L',
            aggregate: true,
            params: {
              securityField: 'Symbol',
              securityFieldProperty: 'QuoteCurrency',
              highlightNegative: true,
            },
          },
          {
            type: 'size',
            field: 'PositiveStressPnL',
            title: 'Positive Stress P&L',
            aggregate: true,
            params: {
              securityField: 'Symbol',
              securityFieldProperty: 'QuoteCurrency',
              highlightNegative: true,
            },
          },
        ] satisfies ColumnDef<CounterpartyMonitoringRow>[]
      ).map(c => [getAgGridColId(c), c])
    );
  }, []);

  return useDefaultColumnsWithGroups({
    defaultColumns,
    columnDefinitions,
    columnGroups: counterpartyMonitoringColumnGroups,
    ungroupedColumnIds: hiddenUngroupedHierarchyColumns,
  });
}
